// ** MUI Imports
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Checkbox,
  FormControl,
  FormLabel,
  FormControlLabel,
  RadioGroup,
  Radio,
  useTheme
} from "@mui/material";

// ** Custom Component Imports
import CustomTextField from "components/mui/text-field";
import CustomAutocomplete from "components/mui/autocomplete";

// ** Third-Party Imports
import { ToastContainer } from 'react-toastify';

const SelectJobs = ({ open, handleClose, JobSelectionProps }) => {
  const theme = useTheme();
  const {
    invoiceType,
    setInvoiceType,
    jobs,
    selectedID,
    selectedJobIds,
    setSelectedJobIds,
    allOptions,
    selectedJobs,
    assessments,
    workOrders,
    depositWorkOrders,
    handleSaveJobs,
    handleChangeSelectedID,
  } = JobSelectionProps;

  return (
    <Dialog
      fullWidth={true}
      maxWidth="sm"
      open={open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #D9D9D9",
          paddingBottom: "20px !important",
        }}
      >
        <Typography variant="h3" fontWeight="bold">
          Select Jobs for Invoice
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={5}>
          <Grid item xs={12}>
            <FormControl>
              <FormLabel
                required
                sx={{
                  color: theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.78) !important' : "rgba(47, 43, 61, 0.78)!important",
                  "& .MuiFormLabel-asterisk": {
                    color: "error.main",
                  },
                  marginBottom: "4px",
                }}
              >
                Select
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="customerTypeLabel"
                name="customerTypeSelection"
                value={invoiceType}
                onChange={(e) => setInvoiceType(e.target.value)}
              >
                <FormControlLabel
                  value="Assessment"
                  control={<Radio color="success" />}
                  label="Assessment"
                />
                <FormControlLabel
                  value="WorkOrder"
                  control={<Radio color="success" />}
                  label="Work Order"
                />
                <FormControlLabel
                  value="WorkOrderDeposit"
                  control={<Radio color="success" />}
                  label="Work Order Deposit"
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          {invoiceType === "Assessment" ? (
            <>
              <Grid item xs={12} md={8}>
                <CustomAutocomplete
                  fullWidth
                  required
                  options={assessments || []}
                  value={
                    (assessments || []).find((data) => data.requestID === selectedID) || null
                  }
                  getOptionLabel={(option) => `${option.preFix}${String(option.requestID).padStart(3, '0')} | ${option.requestTitle}` || ""}
                  onChange={(event, selectedItem) => {
                    handleChangeSelectedID(selectedItem)
                  }}
                  renderInput={(params) => (
                    <CustomTextField
                      {...params}
                      required
                      label="Assessment ID"
                      placeholder="Select"
                    />
                  )}
                />
              </Grid>
            </>
          ) : invoiceType === 'WorkOrderDeposit' ? (
            <>
              <Grid item xs={12} md={8}>
                <CustomAutocomplete
                  fullWidth
                  required
                  options={depositWorkOrders || []}
                  value={
                    (depositWorkOrders || []).find((data) => data.requestID === selectedID) || null
                  }
                  getOptionLabel={(option) => `${option.preFix}${String(option.requestID).padStart(3, '0')} | ${option.requestTitle}` || ""}
                  onChange={(event, selectedItem) => {
                    handleChangeSelectedID(selectedItem)
                  }}
                  renderInput={(params) => (
                    <CustomTextField
                      {...params}
                      required
                      label="Work Order ID"
                      placeholder="Select"
                    />
                  )}
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} md={8}>
                <CustomAutocomplete
                  fullWidth
                  required
                  options={workOrders || []}
                  value={
                    (workOrders || []).find((data) => data.requestID === selectedID) || null
                  }
                  getOptionLabel={(option) => `${option.preFix}${String(option.requestID).padStart(3, '0')} | ${option.requestTitle}` || ""}
                  onChange={(event, selectedItem) => {
                    handleChangeSelectedID(selectedItem)
                  }}
                  renderInput={(params) => (
                    <CustomTextField
                      {...params}
                      required
                      label="Work Order ID"
                      placeholder="Select"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <CustomAutocomplete
                  fullWidth
                  required
                  multiple
                  disableCloseOnSelect
                  value={selectedJobIds.map((id) => ({
                    id,
                    label:
                      jobs.find((j) => j.jobID === id)?.serviceName || "",
                  }))}
                  onChange={(event, newValue) => {
                    const selectAllPresent = newValue.some(
                      (opt) => opt === "Select All"
                    );

                    if (selectAllPresent) {
                      setSelectedJobIds(
                        selectedJobIds.length === jobs.length
                          ? []
                          : jobs.map((job) => job.jobID)
                      );
                      return;
                    }
                    const newSelectedIds = newValue.map((opt) => opt.id);
                    setSelectedJobIds(newSelectedIds);
                  }}
                  options={allOptions}
                  isOptionEqualToValue={(option, value) => {
                    if (option === "Select All" || value === "Select All") {
                      return option === value;
                    }
                    return option.id === value.id;
                  }}
                  getOptionLabel={(option) =>
                    option === "Select All" ? "Select All" : option.label
                  }
                  renderTags={() => (
                    <Box sx={{ pl: 2 }}>
                      {selectedJobIds.length > 0
                        ? `${selectedJobIds.length} Job${
                            selectedJobIds.length !== 1 ? "s" : ""
                          } selected`
                        : ""}
                    </Box>
                  )}
                  renderOption={(props, option, { selected }) => (
                    <>
                      {jobs.length > 0 ? (
                        <li {...props}>
                          <Checkbox
                            checked={
                              option === "Select All"
                                ? selectedJobIds.length === jobs.length
                                : selectedJobIds.includes(option.id)
                            }
                            sx={{ marginRight: 1 }}
                            color="success"
                          />
                          {option === "Select All"
                            ? "Select All"
                            : option.label}
                        </li>
                      ) : (
                        <li>
                          <Typography sx={{ mx: 4, my: 2 }} color="secondary">
                            No jobs to display
                          </Typography>
                        </li>
                      )}
                    </>
                  )}
                  renderInput={(params) => (
                    <CustomTextField
                      {...params}
                      required
                      label="Select Job(s)"
                      placeholder={selectedJobs.length > 0 ? "" : "Select"}
                    />
                  )}
                  ListboxProps={{
                    style: {
                      maxHeight: "200px",
                      overflowY: "auto",
                    },
                  }}
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "20px 24px !important",
          boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
          zIndex: 1,
        }}
        className="dialog-actions-dense"
      >
        <Box>
          <Button variant="contained" color="info" onClick={handleClose}>
            Cancel
          </Button>
        </Box>
        <Box>
          <Button variant="contained" color="main" onClick={handleSaveJobs}>
            Save
          </Button>
        </Box>
      </DialogActions>

      {/*<ToastContainer />*/}
    </Dialog>
  );
};

export default SelectJobs;
